import React, { useState, useEffect } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ResetPasswordData } from '../../types/auth';
import { UnifiedLoadingSpinner } from '../ui';

interface PasswordResetFormProps {
  showTitle?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  showTitle = true,
  onSuccess,
  onCancel,
}) => {
  const { resetUserPassword, error, clearError } = useAuth();
  const { showSuccess, showError } = useToast();
  // ローカルのisLoading: フォーム送信中の状態を管理（AuthContextのグローバルisLoadingとは別目的）
  // AuthContext.isLoadingは認証状態の初期化や全体的な認証処理を管理
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<ResetPasswordData>({
    email: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<ResetPasswordData>>({});
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState<number>(0);
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const RATE_LIMIT_DELAY = 60000; // 1分間のレート制限

  // レート制限の残り時間をリアルタイムで更新
  useEffect(() => {
    if (lastRequestTime > 0) {
      const interval = setInterval(() => {
        const now = Date.now();
        const remaining = Math.ceil((RATE_LIMIT_DELAY - (now - lastRequestTime)) / 1000);
        setRemainingTime(Math.max(0, remaining));
        
        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [lastRequestTime]);

  // メールアドレスのバリデーション
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // フォームバリデーション
  const validateForm = (): boolean => {
    const errors: Partial<ResetPasswordData> = {};

    if (!formData.email.trim()) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!isValidEmail(formData.email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // レート制限チェック
  const checkRateLimit = (): boolean => {
    if (remainingTime > 0) {
      showError(`短時間での連続リクエストは制限されています。${remainingTime}秒後に再試行してください。`, 'レート制限');
      return false;
    }
    return true;
  };

  // 入力値変更ハンドラー
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // エラーをクリア
    if (formErrors[name as keyof ResetPasswordData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // フォーム送信ハンドラー
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('入力内容を確認してください', 'フォームエラー');
      return;
    }

    if (!checkRateLimit()) {
      return;
    }

    setIsLoading(true);
    try {
      setLastRequestTime(Date.now());
      await resetUserPassword(formData);
      setIsEmailSent(true);
      // セキュリティのため、メールアドレスの存在に関係なく同じメッセージを表示
      showSuccess('パスワードリセットの手続きを開始しました', 'リクエスト完了');
      onSuccess?.();
    } catch (err) {
      // エラーはAuthContextで処理済み
      console.error('Password reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // 再送信ハンドラー
  const handleResend = async () => {
    if (!checkRateLimit()) {
      return;
    }
    setIsLoading(true);
    try {
      setLastRequestTime(Date.now());
      await resetUserPassword(formData);
      showSuccess('パスワードリセットの手続きを再開しました', 'リクエスト再開完了');
    } catch (err) {
      console.error('Password reset resend error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // 成功画面
  if (isEmailSent) {
    return (
      <div className="w-full max-w-md mx-auto text-center">
        {showTitle && <h2 className="text-2xl font-bold mb-6 text-gray-800">リクエスト完了</h2>}

        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-6 rounded mb-6">
          <div className="flex items-center justify-center mb-4">
            <svg
              className="w-12 h-12 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-sm mb-2">パスワードリセットの手続きを開始しました。</p>
          <p className="text-sm font-medium">{formData.email}</p>
        </div>

        <div className="text-sm text-gray-600 mb-6">
          <p className="mb-2">
            このメールアドレスが登録されている場合、パスワードリセット用のメールをお送りします。
          </p>
          <p>メールが届かない場合は、迷惑メールフォルダもご確認ください。</p>
          <p className="mt-2 text-xs text-gray-500">
            ※セキュリティのため、メールアドレスの存在確認は行っていません。
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleResend}
            disabled={isLoading}
            className={`w-full py-2 px-4 border border-green-600 rounded-md text-green-600 font-medium hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? '送信中...' : '手続きを再開'}
          </button>

          {/* レート制限の残り時間表示 */}
          {remainingTime > 0 && (
            <div className="text-xs text-gray-500">
              次のリクエストまで {remainingTime} 秒待機中
            </div>
          )}

          {onCancel && (
            <button
              onClick={onCancel}
              className="w-full py-2 px-4 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              戻る
            </button>
          )}
        </div>
      </div>
    );
  }

  // フォーム画面
  return (
    <div className="w-full max-w-md mx-auto">
      {showTitle && (
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">パスワードリセット</h2>
      )}

      <div className="text-sm text-white mb-6">
        <p>
          登録されているメールアドレスを入力してください。
          パスワードリセット用のリンクをお送りします。
        </p>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <p className="font-medium">エラーが発生しました</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={clearError}
            className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
          >
            エラーをクリア
          </button>
        </div>
      )}

      {/* フォーム */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-white mb-1">
            メールアドレス
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-blue-500 ${
              formErrors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="example@email.com"
            disabled={isLoading}
          />
          {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
        </div>

        <div className="flex space-x-3">
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-2 px-4 rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center ${
              isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isLoading ? (
              <>
                <UnifiedLoadingSpinner size="sm" color="white" variant="spinner" className="mr-2" />
                送信中...
              </>
            ) : (
              'リセットメールを送信'
            )}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              キャンセル
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PasswordResetForm;
